/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

// Assets
import { CloseIcon, ArrowLeftIcon } from 'assets/images/Icons'

// Constants
import { CHAIN_TYPE_SIMPLIFIED_OPTIONS, DTI_SIMPLIFIED_MAPPING, TRAVELRULE_METHODS } from 'common/constants/formOptions'
import { TRAVELRULE_NAVIGATION_HEADERS } from 'common/constants/travelRule'

// Hooks
import { useCreateTransfer, useFetchVasp } from 'core/hooks/api'
import { useLocalStorage } from 'core/hooks/storage'

// Store
import { actions } from 'core/store'

// Styled Elements
import {
  CreateEntityDrawerWrapper,
  BackButtonWrapper,
  DrawerNavWrapper,
  DrawerHeader,
  DrawerContent,
  DrawerFooter,
  DrawerCloseIcon,
  DrawerTitle,
  DrawerError,
} from './TransferDrawer.elements'

// Views
import { Drawer, Button, FormNavigation } from 'views/components'

import AmountTransferForm from '../../Forms/TransferForms/AmountTransferForm'
import VaspTransferForm from '../../Forms/TransferForms/VaspTransferForm'
import AccountTransferForm from '../../Forms/TransferForms/AccountTransferForm'
import RecipientTransferForm from '../../Forms/TransferForms/RecipientTransferForm'
import SummaryTransferForm from '../../Forms/TransferForms/SummaryTransferForm'

// Map Redux Props
const mapStateToProps = (state) => state
const mapDispatchToProps = actions

const defaultFormData = {
  transfer: {
    asset_code: CHAIN_TYPE_SIMPLIFIED_OPTIONS[0].value,
    dti: DTI_SIMPLIFIED_MAPPING?.[CHAIN_TYPE_SIMPLIFIED_OPTIONS[0].value]?.dti,
    protocol_code: TRAVELRULE_METHODS[0].value,
  },
  account: null,
  recipient: null,
  vasp: null,
}
function TransferDrawer(props) {
  // Destructure
  const { ui, actions, form } = props

  // Store State
  const { isTransferDrawerOpen } = ui
  const { createdAccount, createdRecipient, createdVasp } = form

  // Store Actions
  const {
    setCreatedAccount,
    setCreatedRecipient,
    setCreatedVasp,
    setIsTransferDrawerOpen,
    setIsPageTableUpdated,
    showAlert,
  } = actions

  // Hooks
  const {
    createTransfer,
    isTransferCreateSuccess,
    isTransferCreateError,
    isTransferCreateLoading,
    transferCreateError,
  } = useCreateTransfer()
  const { getVasp, vaspData, isVaspLoading } = useFetchVasp()

  // State
  const [disabledContinue, setDisabledContinue] = useState(true)
  const [formPage, setFormPage] = useState(1)
  const [recipientAddress, setRecipientAddress] = useState()
  const [recipientVaspOptions, setRecipientVaspOptions] = useState()
  const [formData, setFormData] = useState(defaultFormData)
  const [errorMsg, setErrorMsg] = useState()
  const [isInvalidTransfer, setIsInvalidTransfer] = useState(false)
  const [userCredentials] = useLocalStorage('userCredentials', false)

  // Functions
  const handleBack = () => {
    if (formPage <= 1) return
    if (
      formPage === 5 &&
      (formData?.transfer?.selected_account_address?.is_self_hosted ||
        formData?.transfer?.selected_recipient_address?.is_self_hosted)
    ) {
      setFormPage(3)
      return
    }
    setFormPage((prevState) => prevState - 1)
  }
  const handleContinue = () => {
    if (formPage >= 5) {
      createTransfer({
        amount: Number(formData.transfer.amount),
        amount_usd: formData.transfer.amount_usd,
        dti: formData.transfer.dti,
        // reference_id: '', # NOTE: reference_id is optional, and should have separate form input
        protocol_code: formData.transfer.protocol_code,
        tx_hash: formData.transfer.tx_hash,
        in_address_id: formData.transfer.selected_account_address.id,
        out_address_id: formData.transfer.selected_recipient_address.id,
        in_account_id: formData.account.id,
        out_account_id: formData.recipient.id,
        out_business_id: formData.vasp.id,
      })
      return
    }
    if (
      formPage === 3 &&
      (formData?.transfer?.selected_account_address?.is_self_hosted ||
        formData?.transfer?.selected_recipient_address?.is_self_hosted)
    ) {
      getVasp({ vasp_id: userCredentials?.business_id })
      return
    }
    setFormPage((prevState) => prevState + 1)
  }
  const handleCloseDrawer = () => {
    setFormData({
      transfer: {},
      account: null,
      recipient: null,
      vasp: null,
    })
    setFormPage(1)
    setIsTransferDrawerOpen(false)
  }
  const handleSelfhostedTransfer = () => {
    if (
      formData?.transfer?.selected_account_address?.is_self_hosted &&
      formData?.transfer?.selected_recipient_address?.is_self_hosted
    ) {
      setErrorMsg('Both addresses cannot be self-hosted')
      setIsInvalidTransfer(true)
      return
    }
    if (formData?.transfer?.selected_account_address?.is_self_hosted) {
      setFormData({ ...formData, transfer: { ...formData?.transfer, protocol_code: 'flow' }, vasp: { ...vaspData } })
      setFormPage(5)
      return
    }
    if (formData?.transfer?.selected_recipient_address?.is_self_hosted) {
      setFormData({
        ...formData,
        transfer: { ...formData?.transfer, protocol_code: 'flow' },
        vasp: {
          id: process.env.REACT_APP_SLFHST_VASP_UUID,
          name_legal: 'SELF-HOSTED',
          website: '-',
          address_country_code: '-',
          address_country_desc: '-',
        },
      })
      setFormPage(5)
    }
  }

  // UseEffects
  useEffect(() => {
    // for self-hosted transfers
    handleSelfhostedTransfer()
  }, [vaspData])
  useEffect(() => {
    if (createdAccount) {
      setFormData({
        ...formData,
        account: createdAccount,
        transfer: { ...formData.transfer, account_id: createdAccount.id },
      })
      setCreatedAccount(null)
    }
    if (createdRecipient) {
      setFormData({
        ...formData,
        recipient: createdRecipient,
        transfer: { ...formData.transfer, out_account_id: createdRecipient.id },
      })
      setCreatedRecipient(null)
    }
    if (createdVasp) {
      setFormData({ ...formData, vasp: createdVasp })
      setCreatedVasp(null)
    }
  }, [createdAccount, createdRecipient, createdVasp])

  useEffect(() => {
    if (formPage !== 5) setDisabledContinue(true)
    if (formPage === 1) {
      if (!formData.transfer?.amount || !formData.transfer?.asset_code || !formData.transfer?.amount_usd) {
        setDisabledContinue(true)
      } else setDisabledContinue(false)
    }
    if (formPage === 2) {
      if (!formData.account || !formData.transfer?.in_hash_address) setDisabledContinue(true)
      else setDisabledContinue(false)
    }
    if (formPage === 3) {
      if (!formData.recipient || !formData.transfer?.out_hash_address) setDisabledContinue(true)
      else setDisabledContinue(false)
    }
    if (formPage === 4) {
      if (!formData.vasp || !formData?.transfer.protocol_code) setDisabledContinue(true)
      else setDisabledContinue(false)
    }

    // Disabling transfers between self hosted wallets
    if (
      formData?.transfer?.selected_account_address?.is_self_hosted &&
      formData?.transfer?.selected_recipient_address?.is_self_hosted
    ) {
      setIsInvalidTransfer(true)
      setErrorMsg('Both addresses cannot be self-hosted')
    } else if (
      !formData?.transfer?.selected_account_address?.is_self_hosted ||
      !formData?.transfer?.selected_recipient_address?.is_self_hosted
    ) {
      setIsInvalidTransfer(false)
      setErrorMsg()
    }
  }, [formData, formPage])

  useEffect(() => {
    if (formPage < 5) return
    if (isTransferCreateSuccess) {
      setIsPageTableUpdated(true)
      showAlert({ type: 'success', message: 'Successfully created transfer' })
      setIsTransferDrawerOpen(false)
      setFormPage(1)
      setFormData({
        transfer: {},
        account: null,
        recipient: null,
        vasp: null,
      })
    } else if (isTransferCreateError) {
      if (transferCreateError?.response?.status === 402) {
        showAlert({
          type: 'error',
          message: 'Interaction between free-tier VASPs is restricted. Upgrade to a paid plan to enable this feature.',
        })
      } else {
        showAlert({ type: 'error', message: 'An unexpected error occurred while creating the transfer' })
      }
      // ! This causes the crashing. We need to standardize error messages
      // else if (transferCreateError?.response?.status < 500) {
      //   console.log(transferCreateError?.response?.data?.error)
      //   showAlert({
      //     type: 'error',
      //     message:
      //       transferCreateError?.response?.data?.error
      //   })
      // }
    }
  }, [isTransferCreateSuccess, isTransferCreateError])
  useEffect(() => {
    if (isTransferDrawerOpen) {
      setFormData(defaultFormData)
    }
  }, [isTransferDrawerOpen])

  return (
    <Drawer open={isTransferDrawerOpen} anchor="right" onClose={handleCloseDrawer}>
      <CreateEntityDrawerWrapper>
        <DrawerNavWrapper>
          <BackButtonWrapper>
            {formPage > 1 && (
              <Button startIcon={<ArrowLeftIcon size={30} />} variant="outlined" onClick={handleBack}>
                Back
              </Button>
            )}
          </BackButtonWrapper>
          <DrawerCloseIcon onClick={handleCloseDrawer}>
            <CloseIcon size={18} />
          </DrawerCloseIcon>
        </DrawerNavWrapper>

        <DrawerHeader>
          <DrawerTitle>
            {formPage === 1 && 'Amount'}
            {formPage === 2 && 'Sender'}
            {formPage === 3 && 'Recipient'}
            {formPage === 4 && 'Counterparty VASP'}
            {formPage === 5 && 'Confirm'}
          </DrawerTitle>
          {errorMsg && <DrawerError>{errorMsg}</DrawerError>}
          <FormNavigation
            pageNumber={formPage}
            // setPageNumber={setFormPage}
            headers={TRAVELRULE_NAVIGATION_HEADERS}
          />
        </DrawerHeader>

        <DrawerContent>
          {formPage === 1 && (
            <AmountTransferForm formData={formData} setFormData={setFormData} setErrorMsg={setErrorMsg} />
          )}
          {formPage === 2 && (
            <AccountTransferForm formData={formData} setFormData={setFormData} setErrorMsg={setErrorMsg} />
          )}
          {formPage === 3 && (
            <RecipientTransferForm formData={formData} setFormData={setFormData} setErrorMsg={setErrorMsg} />
          )}
          {formPage === 4 && (
            <VaspTransferForm
              formData={formData}
              setFormData={setFormData}
              setErrorMsg={setErrorMsg}
              recipientAddress={recipientAddress}
              setRecipientAddress={setRecipientAddress}
              recipientVaspOptions={recipientVaspOptions}
              setRecipientVaspOptions={setRecipientVaspOptions}
            />
          )}
          {formPage === 5 && (
            <>
              {isTransferCreateLoading ? (
                <DrawerTitle>{formPage === 5 && isTransferCreateLoading && 'Loading...'}</DrawerTitle>
              ) : (
                <SummaryTransferForm
                  formData={formData}
                  setFormData={setFormData}
                  setErrorMsg={setErrorMsg}
                  setFormPage={setFormPage}
                />
              )}
            </>
          )}
        </DrawerContent>
        <DrawerFooter formPage={formPage}>
          <Button
            disabled={disabledContinue || isTransferCreateLoading || isInvalidTransfer || isVaspLoading}
            onClick={handleContinue}
          >
            {isTransferCreateLoading && 'Creating Transfer...'}
            {!isTransferCreateLoading && !isVaspLoading && formPage === 5 && 'Confirm'}
            {!isTransferCreateLoading && !isVaspLoading && formPage !== 5 && 'Continue'}
            {isVaspLoading && 'Loading...'}
          </Button>
        </DrawerFooter>
      </CreateEntityDrawerWrapper>
    </Drawer>
  )
}

// Default Props
TransferDrawer.defaultProps = {
  ui: {},
  actions: {},
  form: {},
}

// Proptypes Validation
TransferDrawer.propTypes = {
  ui: PropTypes.shape({ isTransferDrawerOpen: PropTypes.bool }),
  actions: PropTypes.shape({
    setIsTransferDrawerOpen: PropTypes.func,
    setCreatedAccount: PropTypes.func,
    setCreatedRecipient: PropTypes.func,
    setCreatedVasp: PropTypes.func,
    setIsPageTableUpdated: PropTypes.func,
    showAlert: PropTypes.func,
    showPersistentAlert: PropTypes.func,
  }),
  form: PropTypes.shape({
    createdAccount: PropTypes.shape({ id: PropTypes.string }),
    createdRecipient: PropTypes.shape({ id: PropTypes.string }),
    createdVasp: PropTypes.shape({}),
  }),
}

export default connect(mapStateToProps, mapDispatchToProps)(TransferDrawer)
